import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { createMap } from './map/mapController.js'
import { createRoomSession } from './map/sync.js'
import { loadIdentity, saveIdentity, ensureUserId } from './lib/identity.js'
import { getRoomById } from './lib/api.js'
import NameColorModal from './ui/NameColorModal.jsx'
import PresencePanel from './ui/PresencePanel.jsx'
import FeaturePanel from './ui/FeaturePanel.jsx'
import Toolbar from './ui/Toolbar.jsx'
import SearchBox from './ui/SearchBox.jsx'

export default function Room() {
  const { roomId } = useParams()
  const navigate = useNavigate()

  // Always null on entry: everyone confirms a name/color each time they enter a
  // room (prefilled with whatever they used last).
  const [identity, setIdentity] = useState(null)
  const [meta, setMeta] = useState(null)
  const [people, setPeople] = useState([])
  const [features, setFeatures] = useState([])
  const [selected, setSelected] = useState(null)
  const [status, setStatus] = useState('connecting')
  const [measuring, setMeasuring] = useState(false)
  const [dark, setDark] = useState(false)
  const [copied, setCopied] = useState(false)
  const [notice, setNotice] = useState('')

  const mapDivRef = useRef(null)
  const sessionRef = useRef(null)
  const noticeTimerRef = useRef(null)

  const showNotice = useCallback((msg) => {
    setNotice(msg)
    clearTimeout(noticeTimerRef.current)
    noticeTimerRef.current = setTimeout(() => setNotice(''), 4000)
  }, [])

  useEffect(() => () => clearTimeout(noticeTimerRef.current), [])

  // Room metadata (for the join code). Non-fatal if it 404s.
  useEffect(() => {
    let active = true
    getRoomById(roomId)
      .then((m) => {
        if (active) setMeta(m)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [roomId])

  // The map + collaborative session. Recreated only when identity or room change.
  useEffect(() => {
    if (!identity || !mapDivRef.current) return
    const { map } = createMap(mapDivRef.current, { interactive: true, center: [20, 0], zoom: 2 })
    const session = createRoomSession({
      roomId,
      identity,
      map,
      callbacks: {
        onPresence: setPeople,
        onFeaturesChange: setFeatures,
        onSelect: setSelected,
        onStatus: setStatus,
        onMeasureChange: setMeasuring,
        onNotice: showNotice,
      },
    })
    sessionRef.current = session

    let fitted = false
    const onSync = (synced) => {
      if (synced && !fitted) fitted = session.fitToFeatures()
    }
    session.provider.on('sync', onSync)
    const sizeTimer = setTimeout(() => map.invalidateSize(), 0)

    return () => {
      clearTimeout(sizeTimer)
      session.provider.off('sync', onSync)
      session.destroy()
      map.remove()
      sessionRef.current = null
    }
  }, [identity, roomId, showNotice])

  // Apply the room's per-user element limit once metadata loads. Declared after
  // the session effect so it runs after the session exists on the same render.
  useEffect(() => {
    if (meta && sessionRef.current) {
      sessionRef.current.setMaxFeatures(meta.max_features ?? null)
    }
  }, [meta, identity])

  const onSelect = useCallback((id) => sessionRef.current?.selectFeature(id), [])
  const onLabel = useCallback((id, label) => sessionRef.current?.updateLabel(id, label), [])
  const onStyle = useCallback((id, patch) => sessionRef.current?.updateStyle(id, patch), [])
  const onDelete = useCallback((id) => sessionRef.current?.deleteFeature(id), [])
  const onGoto = useCallback((result) => sessionRef.current?.gotoLocation(result), [])

  function toggleMeasure() {
    // State is reflected via the onMeasureChange callback (so Esc-to-cancel and
    // the button stay in sync).
    sessionRef.current?.toggleMeasure()
  }
  function fit() {
    sessionRef.current?.fitToFeatures()
  }
  function exportGeoJSON() {
    const data = sessionRef.current?.exportGeoJSON()
    if (!data) return
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${meta?.code || 'collab-map'}.geojson`
    a.click()
    URL.revokeObjectURL(url)
  }
  function copyCode() {
    const text = meta?.code
    if (!text || !navigator.clipboard) return
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  // Everyone picks a name/color on the way in (prefilled from last time). The
  // stable per-browser ownerId is merged in so feature ownership survives
  // renames.
  if (!identity) {
    return (
      <NameColorModal
        initial={loadIdentity()}
        title={meta?.code ? `Join room ${meta.code}` : 'Join the map'}
        cta="Enter room"
        onSubmit={(id) => {
          saveIdentity(id)
          setIdentity({ ...id, id: ensureUserId() })
        }}
      />
    )
  }

  return (
    <div className={'room' + (dark ? ' dark' : '')}>
      <div ref={mapDivRef} className="map" />

      <div className="room-topbar">
        <SearchBox onGoto={onGoto} />
        <div className="room-header">
          <button className="room-code" onClick={copyCode} title="Click to copy the join code">
            <span className="code-label">Room</span>
            <strong>{meta?.code || '· · ·'}</strong>
            <span className="copy-hint">{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          <div className={'status status-' + status}>
            <span className="status-dot" /> {status}
          </div>
        </div>
        {notice && <div className="notice-banner">{notice}</div>}
      </div>

      <PresencePanel people={people} />

      <FeaturePanel
        selected={selected}
        features={features}
        onSelect={onSelect}
        onLabel={onLabel}
        onStyle={onStyle}
        onDelete={onDelete}
      />

      <Toolbar
        measuring={measuring}
        onMeasure={toggleMeasure}
        onFit={fit}
        onExport={exportGeoJSON}
        dark={dark}
        onToggleDark={() => setDark((d) => !d)}
        onLeave={() => navigate('/')}
      />
    </div>
  )
}
